import { useId } from 'react';

type LogoTone = 'gradient' | 'white' | 'current';

interface LogoMarkProps {
  className?: string;
  /** gradient = brand blue→cyan; white = solid white; current = inherits currentColor */
  tone?: LogoTone;
  title?: string;
}

/**
 * NEAT Systems mark — the letter "N" drawn as a network graph: four nodes
 * (endpoints) joined by three links. Pure SVG, scales from favicon to billboard.
 */
export const LogoMark = ({ className, tone = 'gradient', title }: LogoMarkProps) => {
  const id = useId();
  const gradId = `neat-grad-${id}`;
  const paint = tone === 'white' ? '#fff' : tone === 'current' ? 'currentColor' : `url(#${gradId})`;

  return (
    <svg
      viewBox="0 0 64 64"
      className={className}
      fill="none"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      {tone === 'gradient' && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#2563eb" />
            <stop offset="0.5" stopColor="#0ea5e9" />
            <stop offset="1" stopColor="#22d3ee" />
          </linearGradient>
        </defs>
      )}
      <g stroke={paint} strokeWidth="6" strokeLinecap="round">
        <path d="M18 48V16" />
        <path d="M18 16L46 48" />
        <path d="M46 48V16" />
      </g>
      <g fill={paint}>
        <circle cx="18" cy="16" r="7" />
        <circle cx="18" cy="48" r="7" />
        <circle cx="46" cy="16" r="7" />
        <circle cx="46" cy="48" r="7" />
      </g>
    </svg>
  );
};

interface LogoLockupProps {
  className?: string;
  /** dark = dark wordmark (light backgrounds); light = white wordmark (dark backgrounds) */
  tone?: 'dark' | 'light';
  markClassName?: string;
  wordmarkClassName?: string;
}

/** Mark + "NEAT Systems" wordmark. */
export const LogoLockup = ({
  className = '',
  tone = 'dark',
  markClassName = 'h-9 w-9',
  wordmarkClassName = 'text-xl',
}: LogoLockupProps) => {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <LogoMark tone="gradient" className={markClassName} />
      <span className={`font-bold tracking-tight ${wordmarkClassName}`}>
        <span className={tone === 'dark' ? 'text-content' : 'text-white'}>NEAT</span>
        <span
          className={`ml-1.5 font-medium ${tone === 'dark' ? 'text-content-subtle' : 'text-slate-300'}`}
        >
          Systems
        </span>
      </span>
    </span>
  );
};

export default LogoMark;
